import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AlarmIcon from '@mui/icons-material/Alarm';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import ImageIcon from '@mui/icons-material/Image';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { truckService } from '../api/api';
import ConfirmDialog from './ConfirmDialog';
import { formatPortalDate } from '../utils/dateFormat';

const emptyForm = {
  title: '',
  reminder_date: '',
  odometer: '0',
  recurring_interval_value: '',
  recurring_interval_unit: 'Months',
  recurring_odometer: '',
  priority: 'None',
  reminder_frequency: '',
  reminder_time: '',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Regina',
  email_enabled: false,
  email_recipients: '',
  additional_details: '',
  notes: '',
  status: 'Active',
  files: [],
};

function formatDate(value) {
  if (!value) return 'No date';
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return formatPortalDate(date);
}

function isImage(file) {
  return String(file?.mime_type || '').startsWith('image/');
}

function priorityColor(priority) {
  const p = String(priority || '').toLowerCase();
  if (p === 'high') return 'error';
  if (p === 'medium') return 'warning';
  if (p === 'low') return 'info';
  return 'default';
}

function appendFormData(form) {
  const payload = new FormData();
  Object.entries(form).forEach(([key, value]) => {
    if (key === 'files') return;
    payload.append(key, value ?? '');
  });
  Array.from(form.files || []).forEach((file) => payload.append('files', file));
  return payload;
}

export default function TruckReminders({ truckId, truck, createOnly = false, onClose, onSaved }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [reminders, setReminders] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(createOnly);
  const [editingReminder, setEditingReminder] = useState(null);
  const [form, setForm] = useState(() => ({ ...emptyForm, odometer: String(truck?.odometer || 0) }));
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteReminder, setDeleteReminder] = useState(null);
  const [deleteFile, setDeleteFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);

  const selectedReminder = useMemo(
    () => reminders.find((reminder) => reminder.id === selectedId) || reminders[0] || null,
    [reminders, selectedId],
  );

  const loadReminders = async () => {
    if (!truckId) return;
    try {
      setLoading(true);
      setError('');
      const res = await truckService.getReminders(truckId);
      const rows = Array.isArray(res.data) ? res.data : [];
      setReminders(rows);
      if (!selectedId && rows[0]) setSelectedId(rows[0].id);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load reminders.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!createOnly) loadReminders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [truckId, createOnly]);

  const openAdd = () => {
    setEditingReminder(null);
    setForm({ ...emptyForm, odometer: String(truck?.odometer || 0) });
    setDialogOpen(true);
  };

  const openEdit = (reminder) => {
    setEditingReminder(reminder);
    setForm({
      title: reminder.title || '',
      reminder_date: reminder.reminder_date || '',
      odometer: String(reminder.odometer ?? 0),
      recurring_interval_value: reminder.recurring_interval_value || '',
      recurring_interval_unit: reminder.recurring_interval_unit || 'Months',
      recurring_odometer: reminder.recurring_odometer || '',
      priority: reminder.priority || 'None',
      reminder_frequency: reminder.reminder_frequency || '',
      reminder_time: reminder.reminder_time || '',
      timezone: reminder.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Regina',
      email_enabled: reminder.email_enabled === true,
      email_recipients: reminder.email_recipients || '',
      additional_details: reminder.additional_details || '',
      notes: reminder.notes || '',
      status: reminder.status || 'Active',
      files: [],
    });
    setDialogOpen(true);
  };

  const update = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const closeEditor = () => {
    if (saving) return;
    setDialogOpen(false);
    onClose?.();
  };

  const saveReminder = async () => {
    if (!form.title.trim()) {
      setError('Title is required.');
      return;
    }
    if (createOnly && !form.reminder_date) {
      setError('Choose a date for the calendar reminder.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const payload = appendFormData(form);
      if (editingReminder) {
        await truckService.updateReminder(truckId, editingReminder.id, payload);
      } else {
        await truckService.createReminder(truckId, payload);
      }
      setDialogOpen(false);
      setEditingReminder(null);
      if (!createOnly) await loadReminders();
      window.dispatchEvent(new Event('dataUpdated'));
      onSaved?.();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to save reminder.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteReminder = async () => {
    if (!deleteReminder?.id) return;
    try {
      await truckService.deleteReminder(truckId, deleteReminder.id);
      setDeleteReminder(null);
      setSelectedId(null);
      await loadReminders();
      window.dispatchEvent(new Event('dataUpdated'));
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to delete reminder.');
    }
  };

  const confirmDeleteFile = async () => {
    if (!deleteFile?.file?.id || !deleteFile?.reminder?.id) return;
    try {
      await truckService.deleteReminderFile(truckId, deleteFile.reminder.id, deleteFile.file.id);
      setDeleteFile(null);
      await loadReminders();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to delete attachment.');
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 0 } }}>
      {!createOnly && <Card sx={{ borderRadius: 2.25, boxShadow: '0 8px 24px rgba(0,31,63,0.08)' }}>
        <CardContent sx={{ p: { xs: 1.15, sm: 1.35, md: 1.55 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: { xs: 1, sm: 1.25 }, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
            <Box>
              <Typography variant="h4" sx={{ color: '#001f3f', fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1rem', sm: '1.18rem', md: '1.3rem' }, lineHeight: 1.05, mb: 0.1 }}>
                <NotificationsActiveIcon /> Reminders
              </Typography>
              <Typography color="text.secondary" sx={{ fontWeight: 700, fontSize: { xs: '0.68rem', sm: '0.76rem' }, lineHeight: 1.25 }}>
                {truck?.name || `Vehicle #${truckId}`} • Current odometer: {(truck?.odometer || 0).toLocaleString()}
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={{ bgcolor: '#001f3f', fontWeight: 900, borderRadius: 1.6, px: 1.55, py: 0.62, minHeight: 36, fontSize: { xs: '0.78rem', sm: '0.86rem' } }}>
              Add Reminder
            </Button>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ border: '1px solid #d8e2ef', borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{ p: 2, bgcolor: '#f8fbff', borderBottom: '1px solid #d8e2ef' }}>
                  <Typography sx={{ fontWeight: 900 }}>Reminder Home</Typography>
                  <Typography variant="caption" color="text.secondary">{loading ? 'Loading…' : `${reminders.length} reminders`}</Typography>
                </Box>
                <Stack sx={{ maxHeight: { xs: 'none', md: 620 }, overflowY: 'auto' }}>
                  {reminders.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                      <AlarmIcon sx={{ fontSize: 42, opacity: 0.4 }} />
                      <Typography>No reminders yet.</Typography>
                    </Box>
                  ) : reminders.map((reminder) => (
                    <Box
                      key={reminder.id}
                      onClick={() => setSelectedId(reminder.id)}
                      sx={{
                        p: 1.5,
                        cursor: 'pointer',
                        borderBottom: '1px solid #edf2f7',
                        bgcolor: selectedReminder?.id === reminder.id ? 'rgba(25,118,210,0.10)' : 'white',
                        '&:hover': { bgcolor: 'rgba(25,118,210,0.06)' },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        <Typography sx={{ fontWeight: 900, color: '#001f3f' }}>{reminder.title}</Typography>
                        <Chip size="small" label={reminder.priority || 'None'} color={priorityColor(reminder.priority)} />
                      </Box>
                      <Typography variant="body2" color="text.secondary">{formatDate(reminder.reminder_date)}</Typography>
                      <Typography variant="caption" color="text.secondary">Odometer: {(reminder.odometer || 0).toLocaleString()}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={8}>
              {!selectedReminder ? (
                <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center', color: 'text.secondary' }}>
                  Select or create a reminder.
                </Paper>
              ) : (
                <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, border: '1px solid #d8e2ef' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 900, color: '#001f3f' }}>View Reminder</Typography>
                      <Typography color="text.secondary">{truck?.name || `Vehicle #${truckId}`}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button variant="outlined" startIcon={<EditIcon />} onClick={() => openEdit(selectedReminder)}>Edit</Button>
                      <Button color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => setDeleteReminder(selectedReminder)}>Delete</Button>
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Title" value={selectedReminder.title || ''} InputProps={{ readOnly: true }} />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography sx={{ fontWeight: 900, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}><AlarmIcon fontSize="small" /> Set Reminder</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Date" value={formatDate(selectedReminder.reminder_date)} InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Odometer" value={(selectedReminder.odometer || 0).toLocaleString()} helperText={`Current Odometer: ${(truck?.odometer || 0).toLocaleString()}`} InputProps={{ readOnly: true }} />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography sx={{ fontWeight: 900, mt: 1, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}><EventRepeatIcon fontSize="small" /> Set Recurring Reminder</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Every" value={selectedReminder.recurring_interval_unit || ''} InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Frequency" value={selectedReminder.recurring_interval_value || ''} InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField fullWidth label="Every Odometer" value={selectedReminder.recurring_odometer || ''} InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth label="Priority" value={selectedReminder.priority || 'None'} InputProps={{ readOnly: true }} />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography sx={{ fontWeight: 900, mt: 1, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}><NotificationsActiveIcon fontSize="small" /> Notification Setting</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Reminder Frequency" value={selectedReminder.reminder_frequency || ''} InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField fullWidth label="Reminder Time" value={selectedReminder.reminder_time || ''} InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField fullWidth label="Time Zone" value={selectedReminder.timezone || ''} InputProps={{ readOnly: true }} />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField fullWidth multiline minRows={2} label="Additional Details" value={selectedReminder.additional_details || ''} InputProps={{ readOnly: true }} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField fullWidth multiline minRows={2} label="Notes" value={selectedReminder.notes || ''} InputProps={{ readOnly: true }} />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 3 }} />
                  <Typography sx={{ fontWeight: 900, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}><AttachFileIcon fontSize="small" /> Attachments</Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {(selectedReminder.files || []).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No images or documents uploaded.</Typography>
                    ) : selectedReminder.files.map((file) => (
                      <Box key={file.id} sx={{ width: 122 }}>
                        <Box
                          onClick={() => setPreviewFile(file)}
                          sx={{
                            width: 122,
                            height: 92,
                            border: '1px solid #d8e2ef',
                            borderRadius: 2,
                            bgcolor: '#f8fafc',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            cursor: 'zoom-in',
                          }}
                        >
                          {isImage(file) ? <Box component="img" src={file.url} alt={file.original_name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <ImageIcon sx={{ fontSize: 42, color: '#90a4ae' }} />}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                          <IconButton size="small" component="a" href={file.url} target="_blank" rel="noreferrer"><OpenInNewIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => setDeleteFile({ reminder: selectedReminder, file })}><DeleteIcon fontSize="small" /></IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>}

      <Dialog open={dialogOpen} onClose={closeEditor} fullWidth maxWidth="md" fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 900, color: '#001f3f' }}>
          {editingReminder ? 'Edit Reminder' : 'Add Reminder'}
          <IconButton onClick={closeEditor} disabled={saving} sx={{ position: 'absolute', right: 10, top: 10 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {createOnly && <Typography sx={{ mb: 2, color: '#655b8c', fontWeight: 800 }}>{truck?.name || 'Selected vehicle'}</Typography>}
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <TextField required fullWidth label="Title" value={form.title} onChange={update('title')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="Date" value={form.reminder_date} onChange={update('reminder_date')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Odometer" value={form.odometer} onChange={update('odometer')} helperText={`Current Odometer: ${(truck?.odometer || 0).toLocaleString()}`} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label="Every" value={form.recurring_interval_unit} onChange={update('recurring_interval_unit')}>
                {['Days', 'Weeks', 'Months', 'Years'].map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Frequency" value={form.recurring_interval_value} onChange={update('recurring_interval_value')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Every Odometer" value={form.recurring_odometer} onChange={update('recurring_odometer')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Priority" value={form.priority} onChange={update('priority')}>
                {['None', 'Low', 'Medium', 'High'].map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Status" value={form.status} onChange={update('status')}>
                {['Active', 'Completed', 'Paused'].map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Reminder Frequency" value={form.reminder_frequency} onChange={update('reminder_frequency')} placeholder="e.g. Once, Daily, 7 days before" />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth type="time" label="Reminder Time" value={form.reminder_time} onChange={update('reminder_time')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Time Zone" value={form.timezone} onChange={update('timezone')} />
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc' }}>
                <FormControlLabel
                  control={<Switch checked={form.email_enabled} onChange={(event) => setForm((prev) => ({ ...prev, email_enabled: event.target.checked }))} />}
                  label={<Typography sx={{ fontWeight: 900, color: '#001f3f' }}>Send Email When This Reminder Is Due</Typography>}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Email Recipients"
                  value={form.email_recipients}
                  onChange={update('email_recipients')}
                  disabled={!form.email_enabled}
                  placeholder="dane@example.com, vu@example.com"
                  helperText="Comma, semicolon, or new-line separated email addresses."
                  sx={{ mt: 1 }}
                />
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} label="Additional Details" value={form.additional_details} onChange={update('additional_details')} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} label="Notes" value={form.notes} onChange={update('notes')} />
            </Grid>
            <Grid item xs={12}>
              <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} sx={{ borderRadius: 2 }}>
                Add Images / Documents
                <input hidden multiple type="file" accept="image/jpeg,image/png,application/pdf" onChange={(event) => setForm((prev) => ({ ...prev, files: Array.from(event.target.files || []) }))} />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                JPG, PNG, or PDF supported. {form.files.length ? `${form.files.length} selected.` : ''}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeEditor} disabled={saving}>Cancel</Button>
          <Button variant="contained" disabled={saving} onClick={saveReminder} sx={{ bgcolor: '#001f3f', fontWeight: 900 }}>
            {saving ? 'Saving…' : 'Save Reminder'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(previewFile)} onClose={() => setPreviewFile(null)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ fontWeight: 900 }}>
          {previewFile?.original_name || 'Attachment'}
          <IconButton onClick={() => setPreviewFile(null)} sx={{ position: 'absolute', right: 10, top: 10 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ textAlign: 'center', bgcolor: '#f8fafc' }}>
          {isImage(previewFile) ? (
            <Box component="img" src={previewFile?.url} alt={previewFile?.original_name} sx={{ maxWidth: '100%', maxHeight: '78vh', objectFit: 'contain' }} />
          ) : (
            <Button variant="contained" component="a" href={previewFile?.url} target="_blank" rel="noreferrer" startIcon={<OpenInNewIcon />}>
              Open Document
            </Button>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteReminder)}
        title="Delete Reminder?"
        description={`This will delete "${deleteReminder?.title || 'this reminder'}" and its attachments.`}
        confirmText="Delete"
        confirmColor="error"
        onConfirm={confirmDeleteReminder}
        onClose={() => setDeleteReminder(null)}
        icon={<DeleteIcon />}
      />

      <ConfirmDialog
        open={Boolean(deleteFile)}
        title="Delete Attachment?"
        description="This will remove this reminder attachment from the database."
        confirmText="Delete"
        confirmColor="error"
        onConfirm={confirmDeleteFile}
        onClose={() => setDeleteFile(null)}
        icon={<DeleteIcon />}
      />
    </Box>
  );
}
